/**
 * OSMDExerciseScene.fingerNumbers - the reference finger numbers drawn beside each note.
 *
 * Each finger number sits with its own note and never inside the staff lines, in the style Sight
 * Reading draws finger numbers with (Fingering/fingerNumberStyle): the right hand's above its
 * staff (or above the note and its stem or beam, when they reach higher), the left hand's below.
 * Placement follows the hand (its voice), not the staff, so a hand written on the other staff
 * keeps its numbers on its own side. OSMD's own fingering drawing is turned off for runs (it lines
 * the numbers up in a row above the whole line), and the numbers it reads from the MusicXML are
 * drawn here instead.
 */
import type Phaser from 'phaser';
import { FINGER_NUMBER_FONT_FAMILY } from 'Fingering/fingerNumberStyle';

export type FingerHand = 'RH' | 'LH';

/** One drawn note's geometry (in the phrase's own coordinates) and its finger. */
export interface FingeredNote {
  hand: FingerHand;
  finger: string;
  /** The note head's centre. */
  x: number;
  /** The note head's top and bottom. */
  headTop: number;
  headBottom: number;
  /** The end of its stem (or beam), when it has one. */
  stemTip?: number;
  /** The top and bottom lines of the staff the note is written on. */
  staffTop: number;
  staffBottom: number;
}

export interface FingerLabel {
  text: string;
  x: number;
  y: number;
  /** Where the label hangs from its point: 1 = from its bottom (above a note), 0 = from its top. */
  originY: 0 | 1;
}

/**
 * Each finger number's place: a right-hand number just above the highest of its note, stem and
 * staff, a left-hand number just below the lowest of them.
 */
export const fingerLabels = (notes: readonly FingeredNote[], gap: number): FingerLabel[] =>
  notes.map((note) => {
    const ends = [
      note.headTop,
      note.headBottom,
      note.staffTop,
      note.staffBottom,
      ...(note.stemTip !== undefined ? [note.stemTip] : []),
    ];
    return note.hand === 'RH'
      ? { text: note.finger, x: note.x, y: Math.min(...ends) - gap, originY: 1 }
      : { text: note.finger, x: note.x, y: Math.max(...ends) + gap, originY: 0 };
  });

/** A digit's width in the finger numbers' typeface, as a fraction of its font size. */
export const FINGER_DIGIT_WIDTH_EM = 0.6;

/** At most this share of the gap between two neighbouring notes is a digit's width. */
const NEIGHBOUR_GAP_SHARE = 0.75;

/** The smallest horizontal distance between two neighbouring notes of the same hand. */
export const closestNeighbourGap = (notes: readonly FingeredNote[]): number => {
  const gapsOf = (hand: FingerHand): number[] => {
    const xs = notes
      .filter((note) => note.hand === hand)
      .map((note) => note.x)
      .sort((a, b) => a - b);
    return xs
      .slice(1)
      .map((x, index) => x - xs[index])
      .filter((gap) => gap > 0);
  };
  return Math.min(Infinity, ...gapsOf('RH'), ...gapsOf('LH'));
};

/**
 * A finger number's font size: the staff's own proportion, raised to a readable floor, but never
 * so large that a digit is wider than its share of the gap to the next note (so every number
 * stays over its own note).
 */
export const fingerNumberSize = (sizes: {
  proportional: number;
  floor: number;
  neighbourGap: number;
}): number =>
  Math.min(
    Math.max(sizes.proportional, sizes.floor),
    (sizes.neighbourGap * NEIGHBOUR_GAP_SHARE) / FINGER_DIGIT_WIDTH_EM,
  );

/** The parts of an OSMD drawn note this reads (structural, so it can be tested with stand-ins). */
interface DrawnNote {
  sourceNote: { Fingering?: { value: string } | null };
  PositionAndShape?: {
    AbsolutePosition: { x: number; y: number };
    BoundingRectangle?: { x: number; y: number; width: number; height: number };
  };
  vfnote?: Array<{
    getStemExtents?: () => { topY: number; baseY: number };
    hasStem?: () => boolean;
  }>;
}

interface DrawnVoiceEntry {
  parentVoiceEntry?: { ParentVoice?: { VoiceId: number } };
  notes: DrawnNote[];
}

interface DrawnSheet {
  MusicPages: Array<{
    MusicSystems: Array<{
      StaffLines: Array<{
        PositionAndShape: { AbsolutePosition: { y: number } };
        Measures: Array<{ staffEntries: Array<{ graphicalVoiceEntries: DrawnVoiceEntry[] }> }>;
      }>;
    }>;
  }>;
}

/** OSMD's drawing unit in the phrase's coordinates. */
const UNIT_PX = 10;

/** A staff has five lines: four line gaps from top to bottom. */
const GAPS_PER_STAFF = 4;

/**
 * The fingered notes of a drawn sheet, by hand (`handOfVoice`), in the phrase's coordinates.
 * The note head comes from OSMD's bounding box, the stem tip from VexFlow, the staff from its
 * top line and `lineGapUnits` (the distance between its lines, in OSMD units).
 */
export const fingeredNotesOf = (
  sheet: DrawnSheet,
  handOfVoice: (voiceId: number) => FingerHand,
  lineGapUnits: number,
): FingeredNote[] =>
  sheet.MusicPages.flatMap((page) =>
    page.MusicSystems.flatMap((system) =>
      system.StaffLines.flatMap((staffLine) => {
        const staffTop = staffLine.PositionAndShape.AbsolutePosition.y * UNIT_PX;
        const staffBottom = staffTop + GAPS_PER_STAFF * lineGapUnits * UNIT_PX;
        return staffLine.Measures.flatMap((measure) =>
          measure.staffEntries.flatMap((staffEntry) =>
            staffEntry.graphicalVoiceEntries.flatMap((entry) =>
              entry.notes.flatMap((note): FingeredNote[] => {
                const finger = note.sourceNote.Fingering?.value;
                const shape = note.PositionAndShape;
                if (!finger || !shape) return [];
                const box = shape.BoundingRectangle ?? { x: -0.5, y: -0.5, width: 1, height: 1 };
                const vf = note.vfnote?.[0];
                const stem = vf?.hasStem?.() !== false ? vf?.getStemExtents?.() : undefined;
                return [
                  {
                    hand: handOfVoice(entry.parentVoiceEntry?.ParentVoice?.VoiceId ?? 1),
                    finger,
                    x: (shape.AbsolutePosition.x + box.x + box.width / 2) * UNIT_PX,
                    headTop: (shape.AbsolutePosition.y + box.y) * UNIT_PX,
                    headBottom: (shape.AbsolutePosition.y + box.y + box.height) * UNIT_PX,
                    ...(stem ? { stemTip: stem.topY } : {}),
                    staffTop,
                    staffBottom,
                  },
                ];
              }),
            ),
          ),
        );
      }),
    ),
  );

/**
 * Draws the labels into the phrase's container; returns them so they can be removed.
 * `screenScale` is how much the container is enlarged on screen (its scale times the camera's).
 */
export const drawFingerLabels = (
  scene: Phaser.Scene,
  container: Phaser.GameObjects.Container,
  labels: readonly FingerLabel[],
  style: { fontSizePx: number; color: string; screenScale: number },
): Phaser.GameObjects.Text[] =>
  labels.map((label) => {
    const text = scene.add
      .text(label.x, label.y, label.text, {
        fontFamily: FINGER_NUMBER_FONT_FAMILY,
        fontSize: `${style.fontSizePx}px`,
        color: style.color,
        // Text is drawn as a bitmap: draw it at the size it is shown, or the phrase's scale
        // blows a small bitmap up and the number goes soft beside the vector notation.
        resolution: Math.max(1, Math.ceil(style.screenScale)),
      })
      .setOrigin(0.5, label.originY);
    container.add(text);
    return text;
  });
